import type { Encounter, Location } from '@medplum/fhirtypes'
import type { Participant } from '@seen/core/models/patients/participant'
import type { ResourceBindingContext } from '@seen/core/resourceBindingRegistry'
import type { Editor } from '@tiptap/react'
import { useEffect, useMemo } from 'react'
import { useMedplumOptional } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'

export interface RegistryVariableContextSources {
  participant?: Participant
  encounter?: Encounter
  location?: Location
}

// The contexts an editor's registry variables may resolve from. The
// practitioner is the signed-in profile, read without useCurrentPractitioner
// (which throws when no one is signed in) so an editor outside a session or a
// Medplum provider simply lacks that context.
export function useRegistryVariableContexts({
  participant,
  encounter,
  location,
}: RegistryVariableContextSources): ResourceBindingContext {
  const profile = useMedplumOptional()?.getProfile()
  const practitioner =
    profile?.resourceType === 'Practitioner' ? profile : undefined
  // Participant model instances are recreated on every cache write.
  const patient = useMemoValue(participant?.toFhirResource())
  return useMemo(
    () => ({ patient, practitioner, encounter, location }),
    [patient, practitioner, encounter, location]
  )
}

export function useSyncRegistryVariableContexts(
  editor: Editor | null,
  contexts: ResourceBindingContext
): void {
  useEffect(() => {
    if (
      !editor ||
      editor.isDestroyed ||
      !editor.schema.nodes.registryVariable
    ) {
      return
    }
    editor.commands.setRegistryVariableContexts(contexts)
  }, [editor, contexts])
}
